import {
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo,
    useRef,
} from 'react';

import AuthContext from '../context/AuthContext';
import { useChatContext } from '../context/ChatContext';

import useChatList from './useChatList';
import useChatSocketEvents from './useChatSocketEvents';
import useTypingEmitter from './useTypingEmitter';
import useUniversalSocketHandlers from '../components/ChatWidget/useUniversalSocketHandlers';
import useMessageActions from './useMessageActions';

const MAX_PRELOAD_CHATS = 20;

const safeLast = (arr) =>
    Array.isArray(arr) && arr.length ? arr[arr.length - 1] : null;

// ✅ Function definition
const useUnifiedChats = () => {
    const { user } = useContext(AuthContext);
    const userId = user?._id ? String(user._id) : '';

    const friends = useMemo(() => user?.friends || [], [user?.friends]);
    const { activeChat: contextActiveChat, togglePin, pinnedIds } = useChatContext();

    // 1. Data Source
    const { serverChats, fetchChats } = useChatList(userId);

    // ✅ State for Dot Menu Actions
    const [archivedIds, setArchivedIds] = useState(new Set());
    const [mutedIds, setMutedIds] = useState(new Set());
    const [blockedIds, setBlockedIds] = useState(new Set());
    const [favouriteIds, setFavouriteIds] = useState(new Set());

    useEffect(() => {
        if (userId) fetchChats();
    }, [userId, fetchChats]);

    // ✅ Listen for server-side chat updates
    useChatSocketEvents({
        userId,
        onNewChat: fetchChats,
    });

    // 2. Local UI state & Messaging
    const [searchTerm, setSearchTerm] = useState('');
    const [unreadMap, setUnreadMap] = useState({});
    const [typingMap, setTypingMap] = useState({});
    const preloadedRef = useRef(new Set());

    const {
        messagesById: messagesByChatId,
        pageById: pageByChatId,
        hasMoreById: hasMoreByChatId,
        newMessageById,
        setNewMessageById,
        loadMessages,
        handleSendMessage,
        handleReactToMessage,
        markMessagesSeen,
        setMessagesById: setMessagesByChatId,
    } = useMessageActions();

    // ✅ Synchronize Socket Events with local unread counts
    useUniversalSocketHandlers(
        userId,
        contextActiveChat?._id ? String(contextActiveChat._id) : null,
        setMessagesByChatId,
        setUnreadMap,
        setTypingMap
    );

    // ------------------------------------------------------------------
    // ✅ Action Handler for Dot Menu
    // ------------------------------------------------------------------
    const handleChatAction = useCallback((chatId, action) => {
        const id = String(chatId);
        switch (action) {
            case 'archive':
                setArchivedIds(prev => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next;
                });
                break;
            case 'pin':
                togglePin(id);
                break;
            case 'mute':
                setMutedIds(prev => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next;
                });
                break;
            case 'favourite':
                setFavouriteIds(prev => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next;
                });
                break;
            case 'block':
                setBlockedIds(prev => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next;
                });
                break;
            case 'clear':
                setMessagesByChatId(prev => ({ ...prev, [id]: [] }));
                break;
            default:
                break;
        }
    }, [togglePin, setMessagesByChatId]);

    // ------------------------------------------------------------------
    // 3. HYBRID LOGIC: Merging Server Chats with Friend Fallbacks
    // ------------------------------------------------------------------
    const mergedChats = useMemo(() => {
        const chatPartnerIds = new Set();
        const activeChats = Array.isArray(serverChats) ? serverChats : [];

        activeChats.forEach(chat => {
            const participants = chat.members || chat.participants || [];
            participants.forEach(p => {
                const pId = p?._id || p;
                if (pId && String(pId) !== userId) {
                    chatPartnerIds.add(String(pId));
                }
            });
        });

        const friendFallbacks = friends
            .filter(f => f && !chatPartnerIds.has(String(f?._id || f)))
            .map(f => ({
                _id: f._id || f,
                type: 'DM',
                members: [user, f],
                participants: [user, f],
                lastMessage: null,
                updatedAt: 0,
                isFriendFallback: true
            }));

        return [...activeChats, ...friendFallbacks];
    }, [serverChats, friends, userId, user]);

    // 4. Preload Messages
    useEffect(() => {
        if (!userId || !mergedChats.length) return;
        const remaining = MAX_PRELOAD_CHATS - preloadedRef.current.size;
        if (remaining <= 0) return;

        mergedChats
            .filter((c) => c?._id && !c.isFriendFallback && !preloadedRef.current.has(String(c._id)))
            .slice(0, remaining)
            .forEach((chat) => {
                const id = String(chat._id);
                loadMessages(id, 1, true, userId, chat.type);
                preloadedRef.current.add(id);
            });
    }, [userId, mergedChats, loadMessages]);

    // 5. Active chat resolution
    const selectedChatId = contextActiveChat?._id ? String(contextActiveChat._id) : null;

    const activeChat = useMemo(() => {
        if (!selectedChatId) return null;
        let found = mergedChats.find((c) => String(c._id) === selectedChatId);
        return found || contextActiveChat || null;
    }, [mergedChats, selectedChatId, contextActiveChat]);

    // 6. Read Receipts
    useEffect(() => {
        if (selectedChatId && userId && markMessagesSeen) {
            markMessagesSeen(selectedChatId, userId);
            setUnreadMap(prev => ({ ...prev, [selectedChatId]: 0 }));
        }
    }, [selectedChatId, userId, markMessagesSeen, messagesByChatId]);

    // 7. Pagination
    const handleLoadMore = useCallback(async (chatId) => {
        const id = chatId ? String(chatId) : selectedChatId;
        if (!id || !hasMoreByChatId?.[id]) return;
        const nextPage = (pageByChatId?.[id] || 1) + 1;
        const chat = mergedChats.find((c) => String(c._id) === id);
        const type = chat?.type || activeChat?.type || 'DM';
        await loadMessages(id, nextPage, false, userId, type);
    }, [selectedChatId, hasMoreByChatId, pageByChatId, mergedChats, activeChat?.type, loadMessages, userId]);

    // ------------------------------------------------------------------
    // ✅ Decoration & Sorting
    // ------------------------------------------------------------------
    const chatsWithMeta = useMemo(() => {
        const pinnedSet = new Set(pinnedIds.map(id => String(id)));

        return mergedChats
            .filter(c => !blockedIds.has(String(c._id)))
            .map((chat) => {
                const id = String(chat._id);
                const loadedLast = safeLast(messagesByChatId?.[id]);

                return {
                    ...chat,
                    lastMessage: loadedLast || chat.lastMessage || chat.latestMessage || null,
                    isGroup: chat.type === 'GROUP' || chat.isGroup === true,
                    isPinned: pinnedSet.has(id),
                    isMuted: mutedIds.has(id),
                    isFavourite: favouriteIds.has(id),
                    isArchived: archivedIds.has(id),
                    unreadCount: unreadMap[id] || 0
                };
            })
            .sort((a, b) => {
                if (a.isPinned && !b.isPinned) return -1;
                if (!a.isPinned && b.isPinned) return 1;

                const aTime = a.lastMessage?.createdAt || a.updatedAt || 0;
                const bTime = b.lastMessage?.createdAt || b.updatedAt || 0;
                return new Date(bTime).getTime() - new Date(aTime).getTime();
            });
    }, [mergedChats, messagesByChatId, pinnedIds, blockedIds, mutedIds, favouriteIds, archivedIds, unreadMap]);

    return {
        chats: chatsWithMeta,
        activeChat,
        selectedChatId,
        messagesByChatId,
        newMessageByChatId: newMessageById,
        setNewMessageById,
        handleSendMessage,
        handleReactToMessage,
        handleTyping: useTypingEmitter(userId, mergedChats),
        handleLoadMore,
        searchTerm,
        setSearchTerm,
        unreadMap,
        unreadCount: Object.entries(unreadMap).reduce((sum, [id, n]) => {
            return (mutedIds.has(id) || archivedIds.has(id)) ? sum : sum + (Number(n) || 0);
        }, 0),
        typingByChatId: typingMap,
        markMessagesSeen,
        refreshChats: fetchChats,
        handleChatAction,
    };
};

// ✅ CRITICAL FIX: Ensure default export exists for 'import useUnifiedChats'
export default useUnifiedChats;